/** Progressive interactions shared by the static home and React preview. */
export function iniciarInteracoesLanding(root: HTMLElement): () => void {
  const abort = new AbortController();
  const { signal } = abort;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  root.classList.add('lp--interativa');

  const bairro = root.querySelector<HTMLElement>('.lp__bairro');
  const modeloInquilino = bairro?.querySelector<SVGSVGElement>('.lp__bairro-inquilino-modelo');
  const caminhantes = new Map<Element, { pessoa: SVGSVGElement; animacao: Animation }>();
  const limparCaminhantes = () => {
    caminhantes.forEach(({ pessoa, animacao }) => { animacao.cancel(); pessoa.remove(); });
    caminhantes.clear();
  };
  bairro?.querySelectorAll<SVGGElement>('[data-bairro-imovel]').forEach((imovel, indice) => {
    imovel.addEventListener('pointerenter', () => {
      if (!bairro || !modeloInquilino || motion.matches || caminhantes.has(imovel) || caminhantes.size >= 8) return;
      const matriz = imovel.getScreenCTM();
      if (!matriz) return;
      const porta = new DOMPoint(59, 98).matrixTransform(matriz);
      const limites = bairro.getBoundingClientRect();
      const x = porta.x - limites.left - 4.5;
      const y = porta.y - limites.top - 15;
      const destino = indice % 2 === 0 ? limites.width + 12 : -12;
      const pessoa = modeloInquilino.cloneNode(true) as SVGSVGElement;
      pessoa.setAttribute('class', 'lp__bairro-inquilino');
      pessoa.style.left = `${x}px`;
      pessoa.style.top = `${y}px`;
      bairro.append(pessoa);
      const duracao = 650 + Math.abs(destino - x) / 35 * 1000;
      const animacao = pessoa.animate([
        { transform: 'translate(0, 0)', opacity: 0, offset: 0 },
        { transform: `translate(0, ${limites.height - 30 - y}px)`, opacity: 1, offset: 650 / duracao },
        { transform: `translate(${destino - x}px, ${limites.height - 30 - y}px)`, opacity: 1, offset: 1 },
      ], { duration: duracao, easing: 'linear', fill: 'forwards' });
      caminhantes.set(imovel, { pessoa, animacao });
      animacao.onfinish = () => { pessoa.remove(); caminhantes.delete(imovel); };
    }, { signal });
  });
  motion.addEventListener('change', limparCaminhantes, { signal });

  const demo = root.querySelector<HTMLElement>('.lp__palco');
  const play = root.querySelector<HTMLButtonElement>('[data-demo-play]');
  const status = root.querySelector<HTMLElement>('[data-demo-status]');
  const bubbles = Array.from(root.querySelectorAll<HTMLElement>('.lp__chat .lp__balao'));
  const field = root.querySelector<HTMLInputElement>('[data-demo-campo]');
  const pasteToast = root.querySelector<HTMLElement>('[data-demo-colar]');
  const home = root.querySelector<HTMLElement>('[data-demo-ios]');
  const phone = root.querySelector<HTMLElement>('.lp__fone');
  const bank = root.querySelector<HTMLElement>('[data-demo-banco]');
  const bankForm = root.querySelector<HTMLElement>('[data-demo-banco-form]');
  const bankSuccess = root.querySelector<HTMLElement>('[data-demo-banco-sucesso]');
  const pix = root.querySelector<HTMLInputElement>('[data-demo-pix]');
  const pay = root.querySelector<HTMLButtonElement>('[data-demo-pagar]');
  const chat = root.querySelector<HTMLElement>('.lp__chat');
  const whatsapp = root.querySelector<HTMLElement>('.lp__fone .lp__zap');
  const messages = ['Comprovante Pix.pdf', '2.500', 'sim'];
  let step = 1;
  let phase: 'compose' | 'reply' | 'guide' | 'ios' | 'bank' | 'paid' | 'ios-return' | 'done' = 'compose';
  let elapsed = 0;
  let visible = false;
  const show = (count: number) => bubbles.forEach((el, i) => {
    if (el.classList.contains('lp__balao--pago')) el.hidden = i >= count;
    el.classList.toggle('lp__mensagem--oculta', i >= count);
    el.setAttribute('aria-hidden', String(i >= count));
  });
  const compose = () => {
    if (!field || !play || !status) return;
    phase = 'compose';
    elapsed = 0;
    field.value = messages[step]!;
    field.placeholder = 'Mensagem';
    play.setAttribute('aria-disabled', 'false');
    status.textContent = 'Toque em enviar ou aguarde: envio automático em 5 segundos após preencher.';
  };
  const send = () => {
    if (phase !== 'compose' || !play || !field || !status) return;
    // The draft is illustrative and readonly; no user data or network request.
    phase = 'reply';
    elapsed = 0;
    field.value = '';
    play.setAttribute('aria-disabled', 'true');
    show(step * 2 + 1);
    if (demo) demo.dataset.demoEtapa = String(step + 1);
    status.textContent = 'O alugando.com está digitando…';
  };
  const reply = () => {
    show((step + 1) * 2);
    step++;
    if (step < messages.length) compose();
    else {
      phase = 'guide';
      elapsed = 0;
      if (field) field.placeholder = 'Mensagem';
      if (status) status.textContent = 'Guia do imposto sobre aluguel pronta. Agora vamos ao Pix Copia e Cola no banco de exemplo.';
    }
  };
  const openBank = () => {
    if (!bank) return;
    phase = 'bank';
    elapsed = 0;
    bank.hidden = false;
    phone?.setAttribute('data-tela', 'banco');
    if (whatsapp) { whatsapp.inert = true; whatsapp.setAttribute('aria-hidden', 'true'); }
    if (pix) pix.value = '';
    if (pay) pay.setAttribute('aria-disabled', 'true');
    if (demo) demo.dataset.demoEtapa = 'banco';
  };
  const payExample = () => {
    if (phase !== 'bank' || !pix?.value) return;
    phase = 'paid';
    elapsed = 0;
    if (bankForm) bankForm.hidden = true;
    if (bankSuccess) bankSuccess.hidden = false;
  };
  const openHome = (returning: boolean) => {
    if (pasteToast) pasteToast.hidden = true;
    phase = returning ? 'ios-return' : 'ios';
    elapsed = 0;
    if (home) home.hidden = false;
    phone?.setAttribute('data-tela', returning ? 'ios-volta' : 'ios-ida');
    if (whatsapp) { whatsapp.inert = true; whatsapp.setAttribute('aria-hidden', 'true'); }
    if (demo) demo.dataset.demoEtapa = 'banco';
  };
  const returnToChat = () => {
    phase = 'done';
    phone?.setAttribute('data-tela', 'conversa');
    if (home) home.hidden = true;
    if (bank) bank.hidden = true;
    if (whatsapp) { whatsapp.inert = false; whatsapp.removeAttribute('aria-hidden'); }
    show(bubbles.length);
    if (chat) chat.scrollTop = chat.scrollHeight;
    if (demo) demo.dataset.demoEtapa = 'pago';
    if (status) status.textContent = 'Imposto sobre aluguel pago na demonstração. Comprovante guardado. Nenhum pagamento real foi feito.';
  };
  pay?.addEventListener('click', payExample, { signal });
  show(2);
  if (demo) demo.dataset.demoEtapa = '1';
  compose();
  play?.addEventListener('click', send, { signal });
  field?.addEventListener('keydown', event => {
    if (event.key === 'Enter') { event.preventDefault(); send(); }
  }, { signal });
  const demoObserver = typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(entries => { visible = entries[0]?.isIntersecting ?? false; }, { threshold: .15 })
    : undefined;
  if (demo && demoObserver) demoObserver.observe(demo);
  else visible = true;
  // Only count time while the demo is visible. Manual send and auto-send
  // share the same guarded transition, so they cannot duplicate a message.
  const demoTick = setInterval(() => {
    if (!visible || document.hidden || phase === 'done') return;
    elapsed += 100;
    if (phase === 'reply') {
      if (elapsed >= (motion.matches ? 100 : 700)) reply();
    } else if (phase === 'compose') {
      if (elapsed >= 5000) send();
    } else if (phase === 'guide') {
      if (elapsed >= 700 && pasteToast) pasteToast.hidden = false;
      if (elapsed >= 2500) openHome(false);
    } else if (phase === 'ios') {
      if (elapsed >= 1600) openBank();
    } else if (phase === 'bank') {
      if (elapsed >= 700 && pix && !pix.value) {
        pix.value = '00020126…BR.GOV.BCB.PIX…6304';
        pay?.setAttribute('aria-disabled', 'false');
      }
      if (elapsed >= 5700) payExample();
    } else if (phase === 'paid' && elapsed >= 1700) {
      openHome(true);
    } else if (phase === 'ios-return' && elapsed >= 1600) {
      returnToChat();
    }
  }, 100);

  const monthDetail = root.querySelector<HTMLElement>('[data-mes-detalhe]');
  const months = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-mes]'));
  months.forEach(button => button.addEventListener('click', () => {
    months.forEach(other => other.setAttribute('aria-pressed', String(other === button)));
    if (monthDetail) monthDetail.textContent = button.dataset.detalhe ?? '';
  }, { signal }));

  const hero = root.querySelector<HTMLElement>('.lp__hero');
  let frame = 0;
  const resetPointer = () => {
    cancelAnimationFrame(frame);
    hero?.style.removeProperty('--mouse-x');
    hero?.style.removeProperty('--mouse-y');
    hero?.style.removeProperty('--phone-x');
    hero?.style.removeProperty('--phone-y');
    hero?.classList.remove('lp__hero--ponteiro');
  };
  hero?.addEventListener('pointermove', event => {
    if (motion.matches || !fine.matches || event.pointerType === 'touch') return;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      const box = hero.getBoundingClientRect();
      const x = (event.clientX - box.left) / box.width;
      const y = (event.clientY - box.top) / box.height;
      hero.style.setProperty('--mouse-x', `${x * 100}%`);
      hero.style.setProperty('--mouse-y', `${y * 100}%`);
      hero.style.setProperty('--phone-x', `${(x - .5) * 10}px`);
      hero.style.setProperty('--phone-y', `${(y - .5) * 8}px`);
      hero.classList.add('lp__hero--ponteiro');
    });
  }, { signal, passive: true });
  hero?.addEventListener('pointerleave', resetPointer, { signal });

  let observer: IntersectionObserver | undefined;
  const reveals = Array.from(root.querySelectorAll<HTMLElement>(
    '.lp__prova .lp__interno, .lp__passo, .lp__controle-grid, .lp__stat, .lp__guia .lp__interno, .lp__preco-cartao, .lp__faq-lado, .lp__cta-final .lp__interno',
  ));
  const setupMotion = () => {
    observer?.disconnect();
    resetPointer();
    reveals.forEach(el => el.classList.remove('lp__revelar'));
    if (motion.matches || !('IntersectionObserver' in window)) return;
    observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.remove('lp__revelar');
        observer?.unobserve(entry.target);
      }
    }), { threshold: .08 });
    reveals.forEach(el => {
      // Never hide content that is already on screen.
      if (el.getBoundingClientRect().top > innerHeight) {
        el.classList.add('lp__revelar');
        observer!.observe(el);
      }
    });
  };
  setupMotion();
  motion.addEventListener('change', setupMotion, { signal });
  return () => {
    abort.abort();
    limparCaminhantes();
    observer?.disconnect();
    clearInterval(demoTick);
    demoObserver?.disconnect();
    if (pasteToast) pasteToast.hidden = true;
    resetPointer();
    reveals.forEach(el => el.classList.remove('lp__revelar'));
    if (bank) bank.hidden = true;
    if (home) home.hidden = true;
    phone?.removeAttribute('data-tela');
    if (bankForm) bankForm.hidden = false;
    if (bankSuccess) bankSuccess.hidden = true;
    if (whatsapp) { whatsapp.inert = false; whatsapp.removeAttribute('aria-hidden'); }
    show(6);
    root.classList.remove('lp--interativa');
  };
}
